import { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE,
  ACTIVITY_BUILDER_DRAWER_ACTIVITY,
  SectionOrSubSection,
} from '../../activity-builder.constants';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import DifferentiationShape from 'approot/shared/differentiation/differentiation-shape';
import IconSettings from 'approot/shared/icons/icon-settings';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { ComponentActivity } from '../component.constants';
import { useActivityBuilderContext } from '../../activity-builder.context';
import { useActivityNumber } from './activity.hooks';
import { ActivityProperties } from './activity.constants';
import {
  ActivityDifferentiationAssessmentDropdown,
  ActivityDifferentiationLessonDropdown,
} from './activity-differentiation-dropdown';
import type { InteractiveLessonSectionOverviewFragment } from '../../lesson-builder.graphql.generated';
import { PStyled } from 'approot/shared/styles/typography.style';
import { getActivityDetails, getActivityNumber } from './activity.utils';
import { ActivitySettingsCheckpoint } from './activity.style';
import * as Styled from './activity-settings-control.style';
import { LESSON_ACTIVITIES, ActivitiesContentType } from 'global/constants';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';
import { updateSectionsOverviewInCache } from 'approot/activity-builder/logic/apollo/update-section-overviews-in-cache';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';

export const ActivitySettingsControl = ({
  id,
  sectionId,
  contentId,
  contentType,
  properties,
}: ComponentActivity & {
  sectionId: string;
  contentId: number;
  contentType: ActivitiesContentType;
}) => {
  const client = useApolloClient();
  const { userClassId } = useGetInteractiveLessonParams();
  const { number } = useActivityNumber({
    contentId,
    sectionId,
    contentType,
  });
  const { openDrawer, clearActiveComponent } = useActivityBuilderContext();

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleOnChange = useCallback(
    (newProperties: Partial<ActivityProperties>) => {
      const updatedProperties = {
        ...properties,
        ...newProperties,
      };

      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: updatedProperties,
          },
        },
        properties: {
          ...properties,
          ...updatedProperties,
        },
        typename: 'ActivitiesActivityComponent',
        onCompleted: () => {
          updateSectionsOverviewInCache(
            client,
            contentId,
            contentType,
            userClassId,
            sectionId,
            updatedProperties
          );
        },
      });
    },
    [
      id,
      sectionId,
      properties,
      contentId,
      contentType,
      client,
      updateComponentProperties,
      userClassId,
    ]
  );

  const handleUpdateComponent = useCallback(
    (differentiation: Differentiation) => {
      const { activitySettings } = properties;
      handleOnChange({
        activitySettings: {
          ...activitySettings,
          differentiation,
          furtherEnrichment:
            differentiation === Differentiation.GreenCircle
              ? false
              : activitySettings.furtherEnrichment,
        },
      });
    },
    [properties, handleOnChange]
  );

  return (
    <Styled.ActivitySettingsControl
      className={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
    >
      {properties.activitySettings.differentiation && (
        <>
          {contentType === LESSON_ACTIVITIES && (
            <TooltipWrapper
              multiline
              hoverHeading="Checkpoint Question"
              hoverText="A checkpoint question is an opportunity for formative assessment."
            >
              <SecondaryButtonIcon
                type="button"
                $noBorder
                $borderRadius={40}
                role="checkbox"
                aria-label="Checkpoint question"
                aria-checked={!!properties.activitySettings.checkPoint}
                onClick={() =>
                  handleOnChange({
                    activitySettings: {
                      ...properties.activitySettings,
                      checkPoint: !properties.activitySettings.checkPoint,
                    },
                  })
                }
                data-selector="icon-tls-checkpoint"
              >
                <ActivitySettingsCheckpoint
                  $isActive={!!properties.activitySettings.checkPoint}
                  id="icon_star"
                  width={24}
                  height={24}
                />
              </SecondaryButtonIcon>
            </TooltipWrapper>
          )}
          <DifferentiationShape
            differentiation={properties.activitySettings.differentiation}
            number={number}
          />
          {contentType === LESSON_ACTIVITIES ? (
            <ActivityDifferentiationLessonDropdown
              value={properties.activitySettings.differentiation}
              onClick={value => handleUpdateComponent(value)}
            />
          ) : (
            <ActivityDifferentiationAssessmentDropdown
              value={properties.activitySettings.differentiation}
              onClick={value => handleUpdateComponent(value)}
            />
          )}
          <SecondaryButtonIcon
            type="button"
            $noBorder
            $borderRadius={40}
            aria-label="Open activity settings"
            onClick={() => {
              clearActiveComponent();
              openDrawer({ componentId: ACTIVITY_BUILDER_DRAWER_ACTIVITY });
            }}
            data-selector="icon-activity-settings"
          >
            <IconSettings width={24} height={24} />
          </SecondaryButtonIcon>
        </>
      )}
    </Styled.ActivitySettingsControl>
  );
};

export const ActivitySettingsSubSectionControl = ({
  section,
  sectionsOverview,
}: {
  section: SectionOrSubSection;
  sectionsOverview: InteractiveLessonSectionOverviewFragment[];
}) => {
  const { differentiation, progressLabel, parentSectionId, isCheckPoint } =
    useMemo(() => {
      return getActivityDetails({
        section,
        sectionsOverview,
      });
    }, [section, sectionsOverview]);

  const number = useMemo(() => {
    return getActivityNumber(parentSectionId || '', sectionsOverview);
  }, [parentSectionId, sectionsOverview]);

  const { openDrawer } = useActivityBuilderContext();

  return (
    <Styled.ActivitySettingsControl
      className={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
    >
      <Styled.ActivitySettingsControlWrapper>
        {differentiation && (
          <DifferentiationShape
            differentiation={differentiation}
            number={number}
          />
        )}
        {isCheckPoint && (
          <Styled.ActivitySettingsControlCheckpoint
            id="icon_star"
            $isActive
            width={24}
            height={24}
          />
        )}
      </Styled.ActivitySettingsControlWrapper>
      <PStyled marginBottom={0}>{progressLabel}</PStyled>
      <SecondaryButtonIcon
        type="button"
        $noBorder
        $borderRadius={40}
        aria-label="Open activity settings"
        onClick={() =>
          openDrawer({ componentId: ACTIVITY_BUILDER_DRAWER_ACTIVITY })
        }
        data-selector="icon-activity-settings"
      >
        <IconSettings width={24} height={24} />
      </SecondaryButtonIcon>
    </Styled.ActivitySettingsControl>
  );
};
